import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BsArchive, BsArchiveFill } from 'react-icons/bs';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

import { Divider, Menu, MenuItem, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { FiFilter } from 'react-icons/fi';
import { FaFilter } from 'react-icons/fa';

import SearchBar from './SearchBar';
import { InputComponent } from '../../form/InputComponent';
import { Button } from 'src/components/Button';
import { Pie } from 'src/components/pie/Pie';
import Div from 'src/components/div/Div';

import { selectActiveAccount, selectSelectedAccount, selectUser } from 'src/reducers/account.reducer';

import { actionsStatus, category, status, urgencyComplexity } from '../constants';
import { getAuthorizedProducts, getVersionIdByName, getVersionName } from 'src/utils/utils';
import { ActionProps } from '../types';

import { COLORS } from 'src/utils/constants';

interface ActionsHeaderProps {
  admin: boolean;
  query: string;
  setQuery: (value: string) => void;
  selectedEntity: any;
  setSelectedEntity: (prevState: object) => void;
  setLoading: (prevState: boolean) => void;
  filters?: {
    urgency?: Array<object>;
    status?: Array<object>;
    category?: Array<object>;
    product?: Array<object>;
    complexity?: Array<object>;
    archived?: Boolean;
  };
  setFilters?: (prevState: object) => void;
  view?: string;
}

interface OptionsProps {
  value: string;
  label: string;
}

const ActionsHeader = (props: ActionsHeaderProps) => {
  const { admin, selectedEntity, setSelectedEntity, filters, setFilters, view, setLoading } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const animatedComponents = makeAnimated();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // this would be used if we are in impersonated
  const activeAccount = useSelector(selectActiveAccount);

  // this would be used in admin panel to decide which account is selected
  const selectedAccount = useSelector(selectSelectedAccount);

  // In case the user is a guest and we share with it an action
  const currentUser = useSelector(selectUser);

  const handleFilterChange = (selectedOptions, name) => {
    setFilters(prevState => ({ ...prevState, [name]: selectedOptions }));
  };

  const isFullyShared = () => {
    return !!currentUser?.sharedItems?.actions?.find(
      action => action.entityId === selectedEntity?._id && action.shareId === `entity-${action.entityId}`
    );
  };

  const getEntitiesOptions = () => {
    if (admin) {
      return selectedAccount?.entities?.map(entity => ({
        label: entity.name,
        value: entity,
      }));
    } else if (currentUser.role !== 'guest') {
      return activeAccount?.entities
        ?.filter(entity => entity.actionPlan.actions.some(action => action.active))
        ?.map(entity => ({
          label: entity.name,
          value: entity,
        }));
    } else {
      return activeAccount?.entities
        ?.filter(entity => currentUser.sharedItems.actions.some(action => action.entityId === entity._id))
        ?.map(entity => ({
          label: entity.name,
          value: entity,
        }));
    }
  };

  const getProductsOptions = () => {
    const products = [];
    if (admin) {
      const productsList = getAuthorizedProducts()
        .filter(product => product.versioning.enabled)
        .map(product => product.id);
      selectedAccount.entities
        .find(entity => entity._id === selectedEntity?._id)
        ?.responses?.forEach(response => {
          productsList.includes(response.surveyId) &&
            response?.versions?.forEach(version => {
              return products.push({
                label: version?.versionName,
                value: version?._id,
              });
            });
        });
      products.push({
        label: selectedEntity.name,
        value: selectedEntity._id,
      });
    } else if (currentUser.role !== 'guest' || isFullyShared()) {
      activeAccount?.entities
        ?.find(entity => entity._id === selectedEntity?._id)
        ?.actionPlan?.actions?.filter(action => action.active)
        .forEach(action => {
          let versionProductId;
          if (!action.versionProductId) {
            versionProductId = getVersionIdByName(selectedEntity?._id, action.relatedProductId, action.versionProductName);
          } else {
            versionProductId = action.versionProductId;
          }
          if (
            !products.find(product => {
              if (action.isEntityScoop) {
                return product.value === action.versionProductId;
              } else {
                return product.value === versionProductId;
              }
            })
          ) {
            products.push(
              action.isEntityScoop
                ? {
                    label: selectedEntity?.name,
                    value: action.versionProductId,
                  }
                : {
                    label: action.versionProductName,
                    value: versionProductId,
                  }
            );
          }
        });
    } else {
      currentUser.sharedItems.actions
        ?.filter(action => action.entityId === selectedEntity?._id)
        .forEach(action => {
          if (!products.find(product => product.value === action.versionProductId)) {
            products.push({
              label: getVersionName(selectedEntity?._id, action.relatedProductId, action.versionProductId),
              value: action.versionProductId,
            });
          }
        });
    }
    return products;
  };

  useEffect(() => {
    if (getEntitiesOptions().length === 1) {
      setLoading(true);
      setSelectedEntity(getEntitiesOptions()[0].value);
    }
  }, []);

  const clientDisplayedData = () => {
    const account = admin ? selectedAccount : activeAccount;
    if (currentUser.role !== 'guest' || isFullyShared()) {
      return account?.entities.find(entity => entity?._id === selectedEntity?._id)?.actionPlan?.actions?.filter(action => action.active);
    } else {
      return account?.entities
        ?.find(entity => currentUser.sharedItems.actions.some(action => action.entityId === selectedEntity._id && entity._id === selectedEntity?._id))
        ?.actionPlan?.actions?.filter(action => action.active)
        ?.filter(action =>
          currentUser.sharedItems.actions.some(elt => elt.actionId === action.id && elt.relatedProductId === action.relatedProductId)
        );
    }
  };

  const counters =
    !admin &&
    selectedEntity &&
    clientDisplayedData()
      .filter((item: ActionProps) => {
        switch (view) {
          case 'actions':
            return !['Demande', 'Audit'].includes(item.category);
          case 'requests':
            return item.category === 'Demande';
          case 'audits':
            return item.category === 'Audit';
          default:
            return true; // Include all items if view is not recognized
        }
      })
      .filter((item: ActionProps) => {
        const { urgency, status, category, product, complexity } = filters;
        // Apply urgency filter
        if (urgency.length > 0 && !urgency.some((option: OptionsProps) => option.value === item.urgency)) {
          return false;
        }
        // Apply status filter
        if (status.length > 0 && !status.some((option: OptionsProps) => option.value === item.status)) {
          return false;
        }
        // Apply category filter
        if (category.length > 0 && !category.some((option: OptionsProps) => option.value === item.category)) {
          return false;
        }
        // Apply product filter
        if (
          product.length > 0 &&
          !product.some((option: OptionsProps) => {
            if (item.isEntityScoop) {
              return option.value === item.versionProductId;
            } else {
              if (item.versionProductId) {
                return option.value === item.versionProductId;
              }
              if (item.versionProductName && !item.versionProductId) {
                const versionId = getVersionIdByName(selectedEntity?._id, item.relatedProductId, item.versionProductName);
                return option.value === versionId;
              }
            }
            return false;
          })
        ) {
          return false;
        }
        // Apply complexity filter
        if (complexity.length > 0 && !complexity.some((option: OptionsProps) => option.value === item.complexity)) {
          return false;
        }
        return true;
      })
      ?.reduce(
        (acc, action) => {
          acc.total++;
          if (action?.status === 'completed') {
            acc.completed++;
          } else if (action?.status === 'ongoing') {
            acc.ongoing++;
          } else {
            acc.pending++;
          }
          return acc;
        },
        { total: 0, pending: 0, ongoing: 0, completed: 0 }
      );

  const hasArchivedActions = !!selectedAccount?.entities
    .find(entity => entity?._id === selectedEntity?._id)
    ?.actionPlan?.actions?.filter(action => action.isArchived).length;

  const handleShowArchivedActions = () => {
    setFilters(prevState => ({ ...prevState, archived: !prevState['archived'] }));
  };

  if (filters.archived && !hasArchivedActions) {
    handleShowArchivedActions();
  }

  const countersData = useMemo(() => {
    return {
      data: [
        {
          icon: actionsStatus.pending.icon,
          color: actionsStatus.pending.color,
          value: counters?.pending ?? 0,
          label: `A traiter`,
        },
        {
          icon: actionsStatus.ongoing.icon,
          color: actionsStatus.ongoing.color,
          value: counters?.ongoing ?? 0,
          label: `En cours`,
        },
        {
          icon: actionsStatus.completed.icon,
          color: actionsStatus.completed.color,
          value: counters?.completed ?? 0,
          label: `Faites`,
        },
      ],
      percentage: counters?.total !== 0 ? Math.round((counters?.completed / counters?.total) * 100) : 0,
    };
  }, [counters]);

  return (
    <HeaderContainer selectedEntity={!!selectedEntity} admin={admin}>
      <Div flex alignItems='center'>
        <SearchBar {...props} placeholder='Rechercher vos actions ...' disabled={!selectedEntity} />

        <InputContainer>
          <InputComponent
            placeholder='Choisir une entité'
            type={'select'}
            options={getEntitiesOptions()}
            onChange={(name, value) => setSelectedEntity(value)}
            sharable={false}
            defaultValue={getEntitiesOptions().length === 1 && getEntitiesOptions()[0]}
            noOptionsMessage={() => 'Aucune entité disponible'}
          />
        </InputContainer>

        {selectedEntity && (
          <>
            <StyledTooltip title='Filtres' arrow>
              <StyledIconButton
                onClick={handleClick}
                size='large'
                sx={{ ml: 2 }}
                aria-controls={open ? 'filters-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}>
                {filters.status.length > 0 ||
                filters.urgency.length > 0 ||
                filters.category.length > 0 ||
                filters.product.length > 0 ||
                filters.complexity.length > 0 ? (
                  <FaFilter />
                ) : (
                  <FiFilter />
                )}
              </StyledIconButton>
            </StyledTooltip>

            {admin && hasArchivedActions && (
              <StyledTooltip title='Archive' arrow>
                <StyledIconButton onClick={handleShowArchivedActions} size='large' sx={{ ml: 1 }}>
                  {filters.archived ? <BsArchiveFill /> : <BsArchive />}
                </StyledIconButton>
              </StyledTooltip>
            )}

            <Menu
              anchorEl={anchorEl}
              id='filters-menu'
              open={open}
              onClose={handleClose}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}>
              <StyledMenuItem>
                <StyledSelect
                  placeholder='Produit'
                  closeMenuOnSelect
                  components={animatedComponents}
                  isMulti
                  value={filters.product}
                  options={getProductsOptions()}
                  onChange={selectedOptions => handleFilterChange(selectedOptions, 'product')}
                />
              </StyledMenuItem>
              <StyledMenuItem>
                <StyledSelect
                  placeholder='Criticité'
                  closeMenuOnSelect
                  components={animatedComponents}
                  isMulti
                  value={filters.urgency}
                  options={urgencyComplexity}
                  onChange={selectedOptions => handleFilterChange(selectedOptions, 'urgency')}
                />
              </StyledMenuItem>

              <StyledMenuItem>
                <StyledSelect
                  placeholder='Statut'
                  closeMenuOnSelect
                  components={animatedComponents}
                  isMulti
                  value={filters.status}
                  options={status}
                  onChange={selectedOptions => handleFilterChange(selectedOptions, 'status')}
                />
              </StyledMenuItem>

              <StyledMenuItem>
                <StyledSelect
                  placeholder='Catégorie'
                  closeMenuOnSelect
                  components={animatedComponents}
                  isMulti
                  value={filters.category}
                  options={category.filter(cat => cat.value !== 'Audit')}
                  onChange={selectedOptions => handleFilterChange(selectedOptions, 'category')}
                />
              </StyledMenuItem>

              <StyledMenuItem>
                <StyledSelect
                  placeholder='Complexité'
                  closeMenuOnSelect
                  components={animatedComponents}
                  isMulti
                  value={filters.complexity}
                  options={urgencyComplexity}
                  onChange={selectedOptions => handleFilterChange(selectedOptions, 'complexity')}
                />
              </StyledMenuItem>

              <Divider />

              <StyledMenuItem>
                <Button
                  type='light'
                  label='réinitialiser les filtres'
                  onClick={() => {
                    setFilters({ urgency: [], status: [], category: [], product: [], complexity: [] });
                  }}
                />
              </StyledMenuItem>
            </Menu>
          </>
        )}
      </Div>

      {!admin && (
        <StackContainer direction='row' spacing={3} visibility={!!selectedEntity} alignItems={'center'}>
          <PieContainer pieSize={'125px'}>
            <Pie percent={countersData.percentage} color={COLORS.AquaMarine} inverted />
          </PieContainer>
          <Stack direction='column' spacing={2}>
            {countersData.data.map((elt, index) => {
              let Icon = elt.icon;
              return (
                <Stack key={index} direction='row' spacing={1} alignItems={'center'}>
                  <Stack direction='row' spacing={1} alignItems={'center'} fontSize={'1.2rem'}>
                    <Icon color={elt.color} size={30} />
                    <Text>{elt.value}</Text>
                  </Stack>
                  <Text>{elt.label}</Text>
                </Stack>
              );
            })}
          </Stack>
        </StackContainer>
      )}
    </HeaderContainer>
  );
};

const Text = styled.h4`
  font-size: 1.3rem;
`;

const PieContainer = styled.div<{ pieSize?: string }>`
  flex: 1;
  height: ${({ pieSize }) => pieSize || '150px'};
  width: ${({ pieSize }) => pieSize || '150px'};
`;

const StyledIconButton = styled(IconButton)`
  font-size: 2rem;
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    &:hover {
      background: none;
    }

    &:active,
    &:focus {
      background: none;
      outline: none;
    }
  }
`;

const StyledSelect = styled(Select)`
  width: 20rem;
`;

const StyledTooltip = styled(Tooltip)`
  font-size: 1.5rem;
`;

const InputContainer = styled.div`
  margin-left: 1.5rem;
  width: 20rem;
`;

const StackContainer = styled(Stack)<{ visibility?: boolean }>`
  position: relative;
  bottom: 50px;
  right: 100px;
  font-size: 1.2rem;
  visibility: ${({ visibility }) => (visibility ? 'visible' : 'hidden')};
`;

const HeaderContainer = styled.div<{ admin: boolean; selectedEntity: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  ${({ admin, selectedEntity }) =>
    admin &&
    !selectedEntity &&
    `
   padding-top : 50px;
  `}
  margin-bottom: 2rem;
`;

export default ActionsHeader;
